import { formatDuration } from '@/utils/duration';
import { useSurveyTimer } from '@/hooks/useSurveyTimer';

interface ElapsedTimerProps {
  startTimestamp?: number;
  endTimestamp?: number;
  /**
   * Inline single-line variant used on the counting screen, where every pixel
   * of height taken by the header is a pixel taken away from the vehicle grid.
   */
  compact?: boolean;
}

/**
 * Elapsed time in HH:MM:SS, derived from timestamps on every render rather than
 * counted up, so it stays correct across screen locks and app suspension.
 */
export function ElapsedTimer({ startTimestamp, endTimestamp, compact = false }: ElapsedTimerProps) {
  const seconds = useSurveyTimer(startTimestamp, endTimestamp);
  const value = formatDuration(seconds);

  if (compact) {
    return (
      <div
        className="font-mono text-2xl font-bold tabular-nums leading-none tracking-tight text-slate-900"
        aria-label="Elapsed time"
        role="timer"
      >
        {value}
      </div>
    );
  }

  return (
    <div className="text-center">
      <div
        className="font-mono text-5xl font-bold tabular-nums tracking-tight text-slate-900"
        aria-label="Elapsed time"
        role="timer"
      >
        {value}
      </div>
      <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Elapsed time</p>
    </div>
  );
}
